import { AlertTriangle, Mail, MessageCircle, Smartphone } from "lucide-react";
import { Skeleton } from "@/components/skeleton";
import { useApi } from "@/hooks/use-api";
import { formatMonthDay, formatWeekday } from "@/lib/i18n";

interface SendFatigueTimelineProps {
  contactId: string;
}

interface CommunicationTimelineEntry {
  id: string;
  channel: "email" | "sms" | "whatsapp" | "instagram" | "messenger" | "unknown";
  direction: "outbound";
  body: string | null;
  timestamp: string;
}

interface CommunicationTimelineResponse {
  periodDays: number;
  threshold: {
    maxPerWeek: number;
    source: "default" | "tenant_variable";
  };
  currentWeekCount: number;
  exceeded: boolean;
  messages: CommunicationTimelineEntry[];
}

const DAY_MS = 24 * 60 * 60 * 1000;

const CHANNEL_CONFIG: Record<CommunicationTimelineEntry["channel"], { label: string; dotClass: string; Icon: typeof Mail }> = {
  email: { label: "Email", dotClass: "bg-[var(--color-primary)]", Icon: Mail },
  sms: { label: "SMS", dotClass: "bg-[var(--color-success)]", Icon: Smartphone },
  whatsapp: { label: "WhatsApp", dotClass: "bg-[var(--color-success)]", Icon: MessageCircle },
  instagram: { label: "Instagram", dotClass: "bg-[var(--color-warning)]", Icon: MessageCircle },
  messenger: { label: "Messenger", dotClass: "bg-[var(--color-info)]", Icon: MessageCircle },
  unknown: { label: "Other", dotClass: "bg-[var(--color-muted-foreground)]", Icon: MessageCircle },
};

function formatDayKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function buildThirtyDayRange(periodDays: number): Date[] {
  const today = new Date();
  const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  start.setUTCDate(start.getUTCDate() - (periodDays - 1));

  return Array.from({ length: periodDays }, (_, index) => {
    const date = new Date(start);
    date.setUTCDate(start.getUTCDate() + index);
    return date;
  });
}

export function SendFatigueTimeline({ contactId }: SendFatigueTimelineProps) {
  const { data, isLoading, error } = useApi<CommunicationTimelineResponse>(
    `/crm/intelligence/contacts/${contactId}/communication-timeline`,
    { enabled: Boolean(contactId) },
  );

  if (isLoading) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        <Skeleton className="h-5 w-48" />
        <Skeleton className="mt-3 h-16 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
        <div className="flex items-center gap-2 text-[var(--color-warning)]">
          <AlertTriangle className="h-4 w-4" />
          <p className="text-sm font-medium">Communication fatigue unavailable</p>
        </div>
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          The mounted communication timeline could not be loaded right now.
        </p>
      </div>
    );
  }

  const days = buildThirtyDayRange(data.periodDays);
  const messagesByDay = new Map<string, CommunicationTimelineEntry[]>();
  for (const message of data.messages) {
    const key = formatDayKey(new Date(message.timestamp));
    const existing = messagesByDay.get(key) ?? [];
    existing.push(message);
    messagesByDay.set(key, existing);
  }

  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-foreground)]">Communication fatigue</h3>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Last {data.periodDays} days of outbound contact attempts with the current weekly cap.
          </p>
        </div>
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Weekly threshold</p>
          <p className="text-sm font-semibold text-[var(--color-foreground)]">
            {data.currentWeekCount} of {data.threshold.maxPerWeek}
          </p>
        </div>
      </div>

      {data.exceeded ? (
        <div className="mt-4 rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/8 px-3 py-3">
          <div className="flex items-center gap-2 text-[var(--color-destructive)]">
            <AlertTriangle className="h-4 w-4" />
            <p className="text-sm font-semibold">This contact is over the weekly send threshold.</p>
          </div>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            Sending again this week risks fatigue. The current tenant limit is {data.threshold.maxPerWeek} outbound message{data.threshold.maxPerWeek === 1 ? "" : "s"} per week.
          </p>
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-3">
          <p className="text-sm font-medium text-[var(--color-foreground)]">Within fatigue threshold</p>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            This contact is still within the configured weekly limit of {data.threshold.maxPerWeek} outbound message{data.threshold.maxPerWeek === 1 ? "" : "s"}.
          </p>
        </div>
      )}

      <div className="mt-4 overflow-x-auto pb-2">
        <div className="flex min-w-max gap-2">
        {days.map((day) => {
          const key = formatDayKey(day);
          const entries = messagesByDay.get(key) ?? [];
          return (
            <div
              key={key}
              className="w-16 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-2"
            >
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-muted-foreground)]">
                {formatWeekday(day)}
              </p>
              <p className="text-[10px] text-[var(--color-muted-foreground)]">
                {formatMonthDay(day)}
              </p>
              <div className="mt-2 flex min-h-8 flex-wrap gap-1">
                {entries.length > 0 ? entries.map((entry) => {
                  const config = CHANNEL_CONFIG[entry.channel];
                  return (
                    <span
                      key={entry.id}
                      title={`${config.label}: ${entry.body ?? "No preview available"}`}
                      aria-label={`${config.label} send`}
                      className={`h-2.5 w-2.5 rounded-full ${config.dotClass}`}
                    />
                  );
                }) : (
                  <span className="text-[10px] text-[var(--color-muted-foreground)]">-</span>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-xs text-[var(--color-muted-foreground)]">
        {Object.entries(CHANNEL_CONFIG).slice(0, 3).map(([channel, config]) => (
          <span key={channel} className="inline-flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${config.dotClass}`} />
            {config.label}
          </span>
        ))}
      </div>
    </section>
  );
}
